import { BookOpen, FileDown, KeyRound, Terminal } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

const DOWNLOADS = [
    {
        icon: BookOpen,
        de: {
            title: "Katalog online blättern",
            desc: "Unser aktueller Fachhandelskatalog (ohne Preisangaben) direkt im Browser durchblättern — inkl. aller technischen Angaben.",
            cta: "Pageflip-Katalog öffnen",
        },
        en: {
            title: "Browse the catalogue online",
            desc: "Leaf through our current trade catalogue (without prices) right in your browser — including all technical details. (Content in German)",
            cta: "Open the flip catalogue",
        },
        href: "/pageflip",
        external: false,
        testid: "download-pageflip-link",
    },
    {
        icon: FileDown,
        de: {
            title: "Katalog als PDF",
            desc: "Den Katalog als PDF herunterladen und offline behalten — ideal für die Nutzung unterwegs oder im Büro.",
            cta: "PDF herunterladen",
        },
        en: {
            title: "Catalogue as PDF",
            desc: "Download the catalogue as a PDF and keep it offline — ideal when you're on the road or in the office. (Content in German)",
            cta: "Download PDF",
        },
        href: "/datenblaetter/katalog_ohne_preise.pdf",
        external: true,
        testid: "download-pdf-link",
    },
    {
        icon: Terminal,
        de: {
            title: "Software & Anleitungen",
            desc: "Programmier-Software für Kopfstationen & Einzelumsetzer sowie alle Bedienungsanleitungen als PDF.",
            cta: "Zu den Downloads",
        },
        en: {
            title: "Software & manuals",
            desc: "Programming software for headends & transmodulators plus all operating manuals as PDFs.",
            cta: "Go to downloads",
        },
        href: "/downloads",
        external: false,
        testid: "download-software-link",
    },
    {
        icon: KeyRound,
        de: {
            title: "Händler-Preislisten",
            desc: "Den vollständigen Katalog mit Händlerpreisen erhalten Sie im Händlerbereich — Anmeldung einfach mit Ihrer Kundennummer.",
            cta: "Zum Händler-Login",
        },
        en: {
            title: "Dealer price lists",
            desc: "You'll find the full catalogue with dealer prices in the dealer area — just sign in with your customer number.",
            cta: "Go to dealer login",
        },
        href: "#haendler",
        external: false,
        testid: "download-haendler-link",
    },
];

export default function Download() {
    const { t } = useLang();
    return (
        <section id="download" data-testid="download" className="scroll-mt-24 py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <Reveal>
                    <Eyebrow>{t("Download & Katalog", "Downloads & catalogue")}</Eyebrow>
                    <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 max-w-2xl">
                        {t("Unser Fachhandelskatalog. Zum Blättern & Laden.", "Our trade catalogue. For browsing & downloading.")}
                    </h2>
                    <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl text-sm sm:text-base">
                        {t(
                            "Ein übersichtlicher Fachhandelskatalog verschafft Ihnen den Überblick über alle verfügbaren Produkte mit technischen Angaben. Alle Katalogartikel sind Lagerware.",
                            "A well-organised trade catalogue gives you an overview of all available products with technical details. All catalogue items are held in stock."
                        )}
                    </p>
                </Reveal>
                <Reveal delay={0.08}>
                    <Link
                        to="/pageflip"
                        data-testid="catalog-teaser"
                        className="group relative mt-10 grid overflow-hidden rounded-3xl bg-slate-950 text-white border border-slate-800 shadow-xl lg:grid-cols-2"
                    >
                        <div className="absolute inset-0 bg-blueprint-dark" aria-hidden="true" />
                        <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-red-600/15 blur-3xl" aria-hidden="true" />
                        <div className="relative order-last flex items-center justify-center px-6 pb-10 pt-2 sm:px-10 lg:order-none lg:py-14">
                            <div className="relative" style={{ perspective: "1400px" }}>
                                <div className="absolute inset-0 -rotate-2 rounded-sm bg-slate-600/50" aria-hidden="true" />
                                <div className="absolute inset-0 rotate-1 rounded-sm bg-slate-400/60" aria-hidden="true" />
                                <div
                                    data-testid="catalog-teaser-book"
                                    className="relative flex w-[264px] sm:w-[400px] lg:w-[440px] shadow-2xl transition-transform duration-500 [transform-style:preserve-3d] group-hover:[transform:rotateX(3deg)_translateY(-8px)]"
                                >
                                    <img
                                        src="/katalogseiten/page-2.jpg"
                                        alt="Fachhandelskatalog — Aufschlagseite links"
                                        loading="lazy"
                                        data-testid="catalog-teaser-page-left"
                                        className="block w-1/2"
                                    />
                                    <img
                                        src="/katalogseiten/page-3.jpg"
                                        alt="Fachhandelskatalog — Aufschlagseite rechts"
                                        loading="lazy"
                                        data-testid="catalog-teaser-page-right"
                                        className="block w-1/2 transition-transform duration-500 origin-left group-hover:[transform:rotateY(-6deg)]"
                                    />
                                    <div className="pointer-events-none absolute inset-y-0 left-1/2 w-10 -translate-x-1/2 bg-gradient-to-r from-black/30 via-black/10 to-black/30" aria-hidden="true" />
                                </div>
                            </div>
                        </div>
                        <div className="relative p-6 sm:p-10 lg:p-14">
                            <Eyebrow light>62 {t("Seiten · Direkt im Browser", "pages · Right in your browser")}</Eyebrow>
                            <h3 className="mt-5 font-display text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight">
                                {t("So sieht der Katalog aus: wie das gedruckte Heft — nur schneller.", "This is what the catalogue looks like: like the printed magazine — just faster.")}
                            </h3>
                            <p className="mt-4 text-slate-400 leading-relaxed text-sm sm:text-base">
                                {t(
                                    "Unser Fachhandelskatalog als echtes Umblätter-Buch: einmal klicken und alle Produkte durchblättern — mit allen technischen Angaben, ohne Preise.",
                                    "Our trade catalogue as a real page-turning book: one click and you can leaf through all the products — with all technical details, without prices."
                                )}
                            </p>
                            <div className="mt-7 flex flex-wrap gap-3">
                                <span
                                    data-testid="catalog-teaser-cta"
                                    className="inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-500 text-white font-semibold text-sm px-6 py-3 transition-colors"
                                >
                                    <BookOpen className="h-4 w-4" />
                                    {t("Jetzt durchblättern", "Browse it now")}
                                </span>
                                <span
                                    data-testid="catalog-teaser-pdf"
                                    className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white/60 text-slate-200 hover:text-white font-semibold text-sm px-6 py-3 transition-colors"
                                >
                                    <FileDown className="h-4 w-4" />
                                    {t("Als PDF (ohne Preise)", "As PDF (without prices)")}
                                </span>
                            </div>
                        </div>
                    </Link>
                </Reveal>
                <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {DOWNLOADS.map((d, i) => (
                        <Reveal key={d.testid} delay={i * 0.08} className="col-span-1">
                            <a
                                href={d.href}
                                {...(d.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                                data-testid={d.testid}
                                className="group flex h-full flex-col rounded-2xl bg-white border border-slate-200 p-7 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                            >
                                <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-red-50 border border-red-100 text-red-700">
                                    <d.icon className="h-5 w-5" />
                                </span>
                                <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{t(d.de.title, d.en.title)}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600 flex-1">{t(d.de.desc, d.en.desc)}</p>
                                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-red-700">
                                    {t(d.de.cta, d.en.cta)}
                                    <FileDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
                                </span>
                            </a>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
